/**
 * Utility to get application version.
 * The version is fetched from the backend API endpoint.
 */

let cachedVersion: string | null = null;
let versionPromise: Promise<string> | null = null;

/**
 * Get the application version from the API endpoint.
 * Falls back to package.json version if API endpoint is not available.
 *
 * @returns Promise that resolves to the version string
 */
export async function getVersion(): Promise<string> {
  // Return cached version immediately if available
  if (cachedVersion) {
    return cachedVersion;
  }

  // If a fetch is already in progress, return that promise
  if (versionPromise) {
    return versionPromise;
  }

  // Start fetching version
  versionPromise = (async (): Promise<string> => {
    try {
      // Try to fetch from API endpoint first
      const response = await fetch('/api/v1/version', {
        cache: 'default',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          const version = data.version?.trim();
          if (version) {
            cachedVersion = version;
            versionPromise = null; // Reset promise after successful fetch
            return version;
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load version from API endpoint:', error);
    }

    // Fallback to package.json version
    try {
      const packageJson = await import('../../package.json');
      cachedVersion = packageJson.version || '1.0.0';
      versionPromise = null; // Reset promise after fallback
      return cachedVersion;
    } catch (error) {
      console.warn('Failed to load version from package.json:', error);
      cachedVersion = '1.0.0';
      versionPromise = null; // Reset promise after final fallback
      return cachedVersion;
    }
  })();

  return versionPromise;
}

/**
 * Get the application version synchronously (uses cached value or fallback).
 * This is useful for components that need the version immediately.
 *
 * @returns Version string
 */
export function getVersionSync(): string {
  return cachedVersion || '1.0.0';
}
